import { useState } from "react";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { useTranslation } from "react-i18next";

// Email validation schema
const emailSchema = z.string()
  .trim()
  .toLowerCase()
  .email({ message: "Please enter a valid email address" })
  .max(254, { message: "Email must be less than 254 characters" });

const motivationalQuotes = [
  "Champions are made when no one is watching.",
  "The only bad workout is the one that didn't happen.",
  "Your body can stand almost anything. It's your mind you have to convince.",
  "Success is what comes after you stop making excuses.",
  "The pain you feel today will be the strength you feel tomorrow.",
  "Train insane or remain the same.",
  "Sweat is just fat crying.",
  "Don't wish for it. Work for it.",
  "The harder you work, the luckier you get.",
  "Every champion was once a contender who refused to give up.",
  "Push yourself because no one else is going to do it for you.",
  "Strength doesn't come from what you can do. It comes from overcoming the things you once thought you couldn't.",
  "Wake up with determination. Go to bed with satisfaction.",
  "The difference between try and triumph is just a little umph!",
  "Your limitation—it's only your imagination.",
  "Great things never come from comfort zones.",
  "Dream it. Wish it. Do it.",
  "Success doesn't just find you. You have to go out and get it.",
  "The harder the battle, the sweeter the victory.",
  "Don't stop when you're tired. Stop when you're done.",
];

type Topic = "nutrition" | "performance" | "psychology";
const ALL_TOPICS: Topic[] = ["nutrition", "performance", "psychology"];

export const NewsletterPopover = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [topics, setTopics] = useState<Topic[]>([...ALL_TOPICS]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(() =>
    motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)]
  );
  const { toast } = useToast();

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setCurrentQuote(motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)]);
    }
  };

  const toggleTopic = (topic: Topic) => {
    setTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    );
  };

  const handleSubscribe = async () => {
    const validationResult = emailSchema.safeParse(email);
    if (!validationResult.success) {
      toast({
        title: t("newsletter.invalidEmail"),
        description: t("newsletter.pleaseValidEmail"),
        variant: "destructive",
      });
      return;
    }
    if (topics.length === 0) {
      toast({
        title: t("newsletter.topics.chooseTopics"),
        description: t("newsletter.topics.atLeastOne"),
        variant: "destructive",
      });
      return;
    }

    const validatedEmail = validationResult.data;
    setIsLoading(true);

    try {
      const { error: dbError } = await supabase
        .from("newsletter_subscribers")
        .insert({ email: validatedEmail, topics, language: i18n.language });

      if (dbError) {
        if (dbError.code === "23505") {
          toast({
            title: t("newsletter.alreadySubscribed"),
            description: t("newsletter.alreadySubscribedDesc"),
          });
        } else if (dbError.code === "23514") {
          toast({
            title: t("newsletter.invalidEmailFormat"),
            description: t("newsletter.pleaseValidEmail"),
            variant: "destructive",
          });
        } else {
          throw dbError;
        }
        setIsLoading(false);
        return;
      }
      // Welcome + first-tip emails fire automatically via the newsletter_send_welcome DB trigger.

      setIsSubscribed(true);
      setEmail("");
      toast({
        title: t("newsletter.successSubscribed"),
        description: t("newsletter.checkInbox"),
      });
    } catch (error) {
      console.error("Subscription error");
      toast({
        title: t("newsletter.subscriptionFailed"),
        description: t("newsletter.somethingWrong"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" className="text-primary hover:text-primary/80 font-semibold px-4 py-2">
          <Mail className="h-4 w-4 mr-2" />
          {t("nav.newsletter")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-6" align="end">
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm italic text-muted-foreground mb-2">"{currentQuote}"</p>
          </div>

          {isSubscribed ? (
            <div className="text-center py-4">
              <p className="text-primary font-semibold">{t("newsletter.thankYou")}</p>
              <p className="text-sm text-muted-foreground mt-2">
                {t("newsletter.topics.topicsHelp")}
              </p>
            </div>
          ) : (
            <>
              <Input
                type="email"
                placeholder={t("newsletter.enterEmail")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background border-border"
                maxLength={254}
              />

              <div className="space-y-2">
                <p className="text-sm font-semibold text-foreground">
                  {t("newsletter.topics.chooseTopics")}
                </p>
                <div className="flex flex-col gap-2">
                  {ALL_TOPICS.map((topic) => (
                    <label key={topic} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={topics.includes(topic)}
                        onChange={() => toggleTopic(topic)}
                        className="h-4 w-4 accent-primary"
                      />
                      <span>{t(`newsletter.topics.${topic}`)}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("newsletter.topics.topicsHelp")}
                </p>
              </div>

              <Button
                onClick={handleSubscribe}
                disabled={isLoading}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {isLoading ? t("newsletter.subscribing") : t("newsletter.subscribe")}
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};